import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import MovieFilterOutlinedIcon from '@mui/icons-material/MovieFilterOutlined';
import FilterListOffOutlinedIcon from '@mui/icons-material/FilterListOffOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import { useAuth } from '../context/AuthContext';
import {
  fetchAllEditingRequests,
  generateProjectTasks,
  generateProjectZip,
  respondToProjectAssignment,
} from '../graphqlService';
import Invitations from './Invitations';
import PageContainer from './ui/PageContainer';
import PageHeader from './ui/PageHeader';
import Section from './ui/Section';
import EmptyState from './ui/EmptyState';
import LoadingState from './ui/LoadingState';
import EditorProjectRow from './EditorProjects/EditorProjectRow';
import QueueSummary from './EditorProjects/QueueSummary';
import {
  EditorProject,
  QUEUE_FILTERS,
  QueueFilter,
  countQueue,
  matchesQueueFilter,
  parseQueueFilter,
  splitQueue,
  taskCounts,
} from './EditorProjects/editorProjectModel';
import type { EditorHomeRedirectState } from '../utils/editorAccount';
import { debugLog } from '../utils/debugLog';
import { getApolloErrorMessage } from '../utils/apolloError';
import { isFinishedStatus } from '../utils/projectStatus';
import { pluralize } from '../utils/format';
import { friendlyError } from './Projects/projectListModel';

/**
 * The API's page size limit. One page holds an editor's whole queue in
 * practice, so the summary counts, the order and the filters cover every
 * project rather than ten at a time; pagination still appears beyond that.
 */
const ITEMS_PER_PAGE = 100;
const DELIVERED_LIST_ID = 'editor-delivered-projects';

/**
 * The editor's projects (/edit-projects): what needs them first (assignments
 * to answer, requested changes), then work in progress, then cuts with the
 * creator. Delivered and cancelled work is folded away below.
 */
const AllEditProjects = () => {
  const [projects, setProjects] = useState<EditorProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: 'accept' | 'download' | undefined }>({});
  const [showClosed, setShowClosed] = useState(false);
  // Declining asks for a reason in an in-app dialog (it was window.prompt).
  const [declineTarget, setDeclineTarget] = useState<{ id: string; name: string } | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [declineError, setDeclineError] = useState<string | null>(null);
  // Generating replaces every task on the project (and resets progress), so
  // it asks first; it used to run straight from the menu.
  const [generateTarget, setGenerateTarget] = useState<{
    id: string;
    name: string;
    taskCount: number;
    completedCount: number;
  } | null>(null);
  // Separate from the target so the copy doesn't change while the dialog fades out.
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generatingTasks, setGeneratingTasks] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = parseQueueFilter(searchParams.get('show'));

  // Why an editor-only account was sent here from a creator page. Shown once:
  // it is dropped from the history entry, so a refresh or Back doesn't repeat it.
  const routeNotice = (location.state as EditorHomeRedirectState | null)?.notice;
  const [redirectNotice, setRedirectNotice] = useState<string | null>(routeNotice ?? null);
  useEffect(() => {
    if (!routeNotice) return;
    setRedirectNotice(routeNotice);
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
  }, [routeNotice, location.pathname, location.search, navigate]);

  // "Now" for the deadline rules, taken when the list arrives.
  const [now, setNow] = useState(() => new Date());
  const applyResponse = useCallback((response: { requests?: EditorProject[] | null; totalPages?: number | null }) => {
    setProjects((response?.requests || []).filter(Boolean));
    setTotalPages(response?.totalPages || 0);
    setNow(new Date());
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const token = await user?.getIdToken(true);
      if (token) {
        applyResponse(await fetchAllEditingRequests(page, ITEMS_PER_PAGE, token));
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setLoadError('We couldn’t load your projects. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [user, page, applyResponse]);

  useEffect(() => {
    if (user) loadProjects();
  }, [user, loadProjects]);

  /** Reloads in place after an action, so the list doesn't blank out. */
  const refreshProjects = async (token: string) => {
    applyResponse(await fetchAllEditingRequests(page, ITEMS_PER_PAGE, token));
  };

  const setFilter = (next: QueueFilter | null) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('show', next);
    else params.delete('show');
    setSearchParams(params, { replace: true, state: location.state });
  };

  const handleAccept = async (project: EditorProject) => {
    if (actionLoading[project.id]) return;
    setActionLoading((prev) => ({ ...prev, [project.id]: 'accept' }));
    setActionError(null);
    const clearBusy = () => setActionLoading((prev) => ({ ...prev, [project.id]: undefined }));
    let token: string | undefined;
    try {
      token = await user?.getIdToken(true);
      if (!token) {
        setActionError('Your session has expired. Sign in again to accept this project.');
        clearBusy();
        return;
      }
      debugLog('Accepting project:', project.id);
      await respondToProjectAssignment(project.id, true, null, token);
    } catch (err: any) {
      console.error('Error accepting project:', err);
      setActionError(friendlyError(new Error(getApolloErrorMessage(err)), 'The project could not be accepted. Please try again.'));
      clearBusy();
      return;
    }
    // Accepted: a failed refresh must not read as a failed accept.
    setToast(`Accepted ${project.projectName || 'the project'}`);
    try {
      await refreshProjects(token);
    } catch (err: any) {
      console.error('Error reloading projects after accepting:', err);
      setActionError('The project was accepted, but the list could not be refreshed. Reload the page to see it.');
    } finally {
      setActionLoading((prev) => ({ ...prev, [project.id]: undefined }));
    }
  };

  const handleDownloadAssets = async (project: EditorProject) => {
    if (actionLoading[project.id]) return;
    setActionLoading((prev) => ({ ...prev, [project.id]: 'download' }));
    setActionError(null);
    try {
      const token = await user?.getIdToken(true);
      if (!token) return;
      debugLog('Downloading project ZIP for project:', project.id);
      const zipResult = await generateProjectZip(project.id, token);
      if (zipResult.success && zipResult.downloadUrl) {
        const link = document.createElement('a');
        link.href = zipResult.downloadUrl;
        link.download = zipResult.filename || 'project.zip';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setActionError(zipResult.message || 'Could not prepare the project download.');
      }
    } catch (err: any) {
      console.error('Error preparing project download:', err);
      setActionError(friendlyError(new Error(getApolloErrorMessage(err)), 'Could not prepare the project download. Please try again.'));
    } finally {
      setActionLoading((prev) => ({ ...prev, [project.id]: undefined }));
    }
  };

  const openGenerateDialog = (project: EditorProject) => {
    if (isFinishedStatus(project.status)) return;
    const { done, total } = taskCounts(project);
    setGenerateError(null);
    setGenerateTarget({
      id: project.id,
      name: project.projectName || 'this project',
      taskCount: total,
      completedCount: done,
    });
    setGenerateOpen(true);
  };

  const handleConfirmGenerate = async () => {
    if (!generateOpen || !generateTarget || generatingTasks) return;
    const target = generateTarget;
    setGeneratingTasks(true);
    setGenerateError(null);
    try {
      const token = await user?.getIdToken(true);
      if (!token) {
        setGenerateError('Your session has expired. Sign in again to generate tasks.');
        return;
      }
      const result = await generateProjectTasks(target.id, token);
      if (!result?.success) {
        // Shown in the dialog, which stays open for a retry.
        setGenerateError(result?.message || 'Tasks could not be generated. Please try again.');
        return;
      }
      debugLog(`Generated ${result.tasksGenerated} tasks for project:`, target.id);
      setGenerateOpen(false);
    } catch (err: any) {
      console.error('Error generating tasks:', err);
      setGenerateError(getApolloErrorMessage(err));
      return;
    } finally {
      setGeneratingTasks(false);
    }

    // The tasks were replaced; a failed refresh must not read as a failed generate.
    try {
      const token = await user?.getIdToken(true);
      if (!token) return;
      await refreshProjects(token);
    } catch (err: any) {
      console.error('Error reloading projects after task generation:', err);
      setActionError('Tasks were generated, but the project list could not be refreshed. Reload the page to see them.');
    }
  };

  // "Suggest task guidance" opens the project's suggestions preview, so the
  // editor sees them before anything is applied (it used to apply them blind).
  const openGuidancePreview = (project: EditorProject) => {
    if (isFinishedStatus(project.status)) return;
    if (taskCounts(project).total === 0) return;
    navigate(`/edit-projects/${project.id}`, { state: { openAIPreview: true } });
  };

  const openDeclineDialog = (project: EditorProject) => {
    setDeclineReason('');
    setDeclineError(null);
    setDeclineTarget({ id: project.id, name: project.projectName || '' });
  };

  // The reason goes to the creator: without one their notification read only
  // "Editor declined project", with nothing to act on.
  const handleConfirmDecline = async () => {
    if (!declineTarget || declining) return;
    setDeclining(true);
    setDeclineError(null);
    let token: string | undefined;
    try {
      token = await user?.getIdToken(true);
      if (!token) {
        setDeclineError('Your session has expired. Sign in again to decline this project.');
        setDeclining(false);
        return;
      }
      await respondToProjectAssignment(declineTarget.id, false, declineReason.trim() || null, token);
    } catch (err: any) {
      console.error('Error declining project:', err);
      // Shown in the dialog, which stays open for a retry.
      setDeclineError(friendlyError(err, 'The project could not be declined. Please try again.'));
      setDeclining(false);
      return;
    }
    // Declined: close the dialog even if the list can't be refreshed, so
    // nobody declines twice.
    setDeclineTarget(null);
    setToast('Project declined. The creator has been told.');
    try {
      await refreshProjects(token);
    } catch (err: any) {
      console.error('Error reloading projects after declining:', err);
      setActionError('The project was declined, but the list could not be refreshed. Reload the page to update it.');
    } finally {
      setDeclining(false);
    }
  };

  // ---- What to show ------------------------------------------------------------

  const { active, closed } = useMemo(() => splitQueue(projects), [projects]);
  const counts = useMemo(() => countQueue(active, now), [active, now]);
  const shownActive = useMemo(
    () => (filter ? active.filter((project) => matchesQueueFilter(project, filter, now)) : active),
    [active, filter, now]
  );
  const activeFilter = filter ? QUEUE_FILTERS.find((option) => option.value === filter) : undefined;
  const allCancelled = closed.length > 0 && closed.every((project) => String(project.status).toUpperCase() === 'CANCELLED');
  const anyCancelled = closed.some((project) => String(project.status).toUpperCase() === 'CANCELLED');
  const closedTitle = allCancelled ? 'Cancelled' : anyCancelled ? 'Delivered and cancelled' : 'Delivered';
  // With nothing open, the finished work is the list.
  const closedExpanded = showClosed || active.length === 0;

  const renderRows = (list: EditorProject[], label: string, id?: string) => (
    <Box component="ul" id={id} aria-label={label} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {list.map((project, index) => (
        <EditorProjectRow
          key={project.id}
          project={project}
          now={now}
          divider={index > 0}
          busy={Boolean(actionLoading[project.id])}
          accepting={actionLoading[project.id] === 'accept'}
          onAccept={(target) => void handleAccept(target)}
          onDecline={openDeclineDialog}
          onGenerateTasks={openGenerateDialog}
          onSuggestGuidance={openGuidancePreview}
          onDownloadAssets={(target) => void handleDownloadAssets(target)}
        />
      ))}
    </Box>
  );

  const renderContent = () => {
    if (loading && projects.length === 0) {
      return <LoadingState variant="list" count={4} label="Loading projects" />;
    }
    if (loadError && projects.length === 0) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void loadProjects()}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      );
    }
    if (projects.length === 0) {
      return (
        <Card>
          <EmptyState
            icon={<MovieFilterOutlinedIcon />}
            title="No projects yet"
            description="When a creator sends you a project, it appears here with the brief, their time-coded feedback and the source files."
          />
        </Card>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {loadError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void loadProjects()}>
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}
        {active.length > 0 || filter ? (
          <Section
            title="Active projects"
            description={<QueueSummary counts={counts} active={filter} openCount={active.length} onChange={setFilter} />}
            action={
              filter ? (
                <Button size="small" onClick={() => setFilter(null)}>
                  Show all
                </Button>
              ) : undefined
            }
            disablePadding
          >
            {shownActive.length > 0 ? (
              renderRows(shownActive, 'Active projects')
            ) : (
              <EmptyState
                compact
                icon={<FilterListOffOutlinedIcon />}
                title={activeFilter?.empty || 'No active projects'}
                description="Projects show up here when they reach this point."
                secondaryActionLabel="Show all projects"
                onSecondaryAction={() => setFilter(null)}
              />
            )}
          </Section>
        ) : (
          <Card>
            <EmptyState
              compact
              icon={<InboxOutlinedIcon />}
              title="No active projects"
              description="New projects from creators appear here. Your finished work is below."
            />
          </Card>
        )}

        {closed.length > 0 && !filter && (
          <Box>
            {active.length > 0 && (
              <Button
                variant="text"
                color="inherit"
                onClick={() => setShowClosed((open) => !open)}
                aria-expanded={closedExpanded}
                aria-controls={DELIVERED_LIST_ID}
                endIcon={closedExpanded ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
                sx={{ mb: closedExpanded ? 1.5 : 0, color: 'text.secondary', fontWeight: 600 }}
              >
                {closedExpanded ? `Hide ${closedTitle.toLowerCase()}` : `Show ${closedTitle.toLowerCase()} (${closed.length})`}
              </Button>
            )}
            {closedExpanded && (
              <Section title={closedTitle} description={pluralize(closed.length, 'project')} disablePadding>
                {renderRows(closed, closedTitle, DELIVERED_LIST_ID)}
              </Section>
            )}
          </Box>
        )}

        {/* Pagination: the API pages at 100, so this only appears past that. */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Counts and order cover this page of projects.
            </Typography>
            <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" shape="rounded" />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Projects"
        subtitle="Work creators have sent you. Anything waiting on you comes first."
      />

      {redirectNotice && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setRedirectNotice(null)} data-testid="creator-only-notice">
          {redirectNotice}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Pending invitations from creators. Editor-only accounts never see
          the creator dashboard, where these are otherwise listed. */}
      <Box sx={{ mb: 3, '&:empty': { display: 'none' } }}>
        <Invitations
          hideWhenEmpty
          title="Editor invitations"
          description="Creators who’d like you to edit for them."
        />
      </Box>

      {renderContent()}

      <Dialog
        open={Boolean(declineTarget)}
        onClose={() => { if (!declining) setDeclineTarget(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Decline this project?</DialogTitle>
        <DialogContent>
          {declineTarget?.name && (
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>{declineTarget.name}</Typography>
          )}
          <TextField
            label="Reason (optional)"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            autoFocus
            inputProps={{ maxLength: 1000 }}
            helperText="Sent to the creator, so they can reassign the project quickly."
          />
          {declineError && <Alert severity="error" sx={{ mt: 2 }}>{declineError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeclineTarget(null)} disabled={declining}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDecline}
            disabled={declining}
            startIcon={declining ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {declining ? 'Declining…' : 'Decline project'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={generateOpen}
        onClose={() => { if (!generatingTasks) setGenerateOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Generate tasks?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {generateTarget && generateTarget.taskCount > 0
              ? `Generate tasks for ${generateTarget.name}? This replaces the project's current tasks and their progress with new tasks generated from the creator's annotations.`
              : `Generate tasks for ${generateTarget?.name ?? 'this project'} from the creator's annotations?`}
          </DialogContentText>
          {generateTarget && generateTarget.taskCount > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Current tasks: {generateTarget.taskCount} ({generateTarget.completedCount} done). Your notes and time tracking on them are removed too.
            </Typography>
          )}
          {generateError && <Alert severity="error" sx={{ mt: 2 }}>{generateError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setGenerateOpen(false)} disabled={generatingTasks}>Cancel</Button>
          <Button
            variant="contained"
            color={generateTarget && generateTarget.taskCount > 0 ? 'error' : 'primary'}
            onClick={handleConfirmGenerate}
            disabled={generatingTasks}
            startIcon={generatingTasks ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {generatingTasks
              ? 'Generating…'
              : generateTarget && generateTarget.taskCount > 0 ? 'Replace tasks' : 'Generate tasks'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </PageContainer>
  );
};

export default AllEditProjects;
